import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { reviewsAPI, employeeAPI } from '../../services/api';
import { toast } from 'sonner';
import { ClipboardList, Star, Clock, Check, Building, Plus, Loader2, ChevronRight } from 'lucide-react';

const EmployeeReviews = () => {
  const [activeTab, setActiveTab] = useState('received');
  const [reviews, setReviews] = useState([]);
  const [requests, setRequests] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reviewsRes, requestsRes, recordsRes] = await Promise.all([
        reviewsAPI.getEmployeeReviews(),
        reviewsAPI.getMyRequests(),
        employeeAPI.getRecords()
      ]);
      setReviews(reviewsRes.data || []);
      setRequests(requestsRes.data || []);
      // Only show acknowledged records with employers for requesting reviews
      setRecords((recordsRes.data || []).filter(r => r.status === 'acknowledged'));
    } catch (error) {
      console.error('Error fetching reviews data:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs flex items-center gap-1"><Clock className="w-3 h-3" />Pending Response</span>;
      case 'acknowledged':
        return <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs flex items-center gap-1"><Check className="w-3 h-3" />Acknowledged</span>;
      case 'accepted':
        return <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">Accepted</span>;
      case 'declined':
        return <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-xs">Declined</span>;
      default:
        return <span className="px-2 py-0.5 rounded-full bg-[#F5F8FF] text-[#5B6472] text-xs">{status}</span>;
    }
  };

  const getCadenceBadge = (cadence) => {
    const colors = {
      'one-time': 'bg-[#F5F8FF] text-[#5B6472]',
      'weekly': 'bg-blue-500/20 text-blue-400',
      'monthly': 'bg-purple-500/20 text-purple-400',
      'quarterly': 'bg-green-500/20 text-green-400'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs ${colors[cadence] || colors['one-time']}`}>
        {cadence?.charAt(0).toUpperCase() + cadence?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  const pendingReviews = reviews.filter(r => r.status === 'pending');

  return (
    <DashboardLayout>
      <div className="space-y-6 sm:space-y-8" data-testid="employee-reviews">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">My Reviews</h1>
            <p className="mt-2 text-[#5B6472]">View performance reviews and request new ones</p>
          </div>
          {records.length > 0 && (
            <Link to="/employee/reviews/request">
              <Button className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl" data-testid="request-review-btn">
                <Plus className="w-4 h-4 mr-2" />
                Request Review
              </Button>
            </Link>
          )}
        </div>

        {/* Pending Reviews Alert */}
        {pendingReviews.length > 0 && (
          <Card className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 animate-fade-in">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-400" />
              <p className="text-amber-400">
                You have {pendingReviews.length} review{pendingReviews.length > 1 ? 's' : ''} waiting for your response
              </p>
            </div>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="animate-fade-in">
          <TabsList className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-1">
            <TabsTrigger value="received" className="rounded-lg data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2">
              Received ({reviews.length})
            </TabsTrigger>
            <TabsTrigger value="requests" className="rounded-lg data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2">
              My Requests ({requests.length})
            </TabsTrigger>
          </TabsList>

          {/* Received Reviews */}
          <TabsContent value="received" className="mt-6">
            {reviews.length > 0 ? (
              <div className="grid gap-4">
                {reviews.map((review) => (
                  <Link key={review.id} to={`/employee/reviews/${review.id}`}>
                    <Card className={`bg-[#FFFFFF] border rounded-xl p-5 hover:border-[#1B5AFF]/50 transition-colors ${
                      review.status === 'pending' ? 'border-amber-500/30' : 'border-[#E8EDF5]'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                            {review.employer_logo ? (
                              <img src={review.employer_logo} alt="" className="w-12 h-12 rounded-full object-cover" />
                            ) : (
                              <Building className="w-6 h-6 text-[#5B6472]" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-[#0A0F1E]">{review.employer_name || 'Employer'}</p>
                            <div className="flex items-center gap-2 mt-1">
                              {getCadenceBadge(review.review_type)}
                              {getStatusBadge(review.status)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="flex items-center gap-0.5 mb-1 justify-end">
                              {[...Array(5)].map((_, i) => (
                                <Star 
                                  key={i} 
                                  className={`w-4 h-4 ${i < review.performance_rating ? 'fill-[#1B5AFF] text-[#1B5AFF]' : 'text-[#5B6472]'}`} 
                                />
                              ))}
                            </div>
                            <p className="text-[#5B6472] text-xs">
                              {new Date(review.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-[#5B6472]" />
                        </div>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
                <ClipboardList className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No reviews yet</h3>
                <p className="text-[#5B6472] mb-4">
                  You haven't received any performance reviews yet.
                </p>
                {records.length > 0 && (
                  <Link to="/employee/reviews/request">
                    <Button className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl">
                      Request a Review
                    </Button>
                  </Link>
                )}
              </Card>
            )}
          </TabsContent>

          {/* My Requests */}
          <TabsContent value="requests" className="mt-6">
            {requests.length > 0 ? (
              <div className="grid gap-4">
                {requests.map((request) => (
                  <Card key={request.id} className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                          {request.employer_logo ? (
                            <img src={request.employer_logo} alt="" className="w-12 h-12 rounded-full object-cover" />
                          ) : (
                            <Building className="w-6 h-6 text-[#5B6472]" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-[#0A0F1E]">{request.employer_name || 'Employer'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {getCadenceBadge(request.preferred_cadence)}
                            {getStatusBadge(request.status)}
                          </div>
                          {request.note && (
                            <p className="text-[#5B6472] text-xs mt-1 italic">"{request.note}"</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[#5B6472] text-xs">
                          Requested {new Date(request.created_at).toLocaleDateString()}
                        </p>
                        {request.status === 'declined' && request.decline_reason && (
                          <p className="text-red-400 text-xs mt-1">{request.decline_reason}</p>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
                <ClipboardList className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No requests yet</h3>
                <p className="text-[#5B6472] mb-4">
                  You haven't requested any performance reviews yet.
                </p>
                {records.length > 0 && (
                  <Link to="/employee/reviews/request">
                    <Button className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl">
                      Request a Review
                    </Button>
                  </Link>
                )}
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default EmployeeReviews;
